import { watch, onScopeDispose, shallowRef } from 'vue'
import { useRoute } from 'vue-router'
import { useRuntimeConfig } from '#app'
import { useIterableCmsPageSlug } from '~/composables/useIterableCmsPageSlug'
import type { IterableLiveListItem } from '~/utils/iterable-live-merge'
import { logIterableError } from '~/utils/iterable-log'

/**
 * Shared client hooks: `DynamicComponents` + `IterableBlock` both call this composable.
 * Iterable content is live via the SDK; these hooks only help Strapi-driven layout (e.g. `messageType`) via live-meta refresh.
 *
 * Ref-count: we only **increment** under `import.meta.client`, and **onScopeDispose** decrements on the
 * client only so SPA navigation tears down poll/focus/visibility listeners when the last consumer unmounts.
 */
let liveMetaClientRefCount = 0
let liveMetaPollTimer: ReturnType<typeof setInterval> | null = null
let liveMetaVisibilityHandler: (() => void) | null = null
let liveMetaFocusHandler: (() => void) | null = null
/** bfcache / Safari: supplement `visibilitychange` */
let liveMetaPageShowHandler: ((event: PageTransitionEvent) => void) | null = null
let liveMetaImmediateDebounce: ReturnType<typeof setTimeout> | null = null
/** Avoid overlapping `refresh()` calls (poll + focus + route debounce). */
let liveMetaRefreshInFlight = false
let liveMetaRefreshPending = false

/** Monotonic counter so an older in-flight `$fetch` cannot commit after a newer slug / refresh started. */
let liveMetaFetchSeq = 0
/** Last payload applied for this key; stale handlers return a clone of this instead of overwriting a newer response. */
const committedLiveMeta = shallowRef<IterableLiveMetaResponse>({ byId: {}, blocks: [] })

function cloneLiveMetaResponse (data: IterableLiveMetaResponse): IterableLiveMetaResponse {
  return { byId: { ...data.byId }, blocks: [...data.blocks] }
}

function stopLiveMetaClientHooks () {
  liveMetaRefreshPending = false
  if (liveMetaPollTimer != null) {
    clearInterval(liveMetaPollTimer)
    liveMetaPollTimer = null
  }
  if (liveMetaVisibilityHandler != null && typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', liveMetaVisibilityHandler)
    liveMetaVisibilityHandler = null
  }
  if (liveMetaFocusHandler != null && typeof window !== 'undefined') {
    window.removeEventListener('focus', liveMetaFocusHandler)
    liveMetaFocusHandler = null
  }
  if (liveMetaPageShowHandler != null && typeof window !== 'undefined') {
    window.removeEventListener('pageshow', liveMetaPageShowHandler)
    liveMetaPageShowHandler = null
  }
  if (liveMetaImmediateDebounce != null) {
    clearTimeout(liveMetaImmediateDebounce)
    liveMetaImmediateDebounce = null
  }
}

function startLiveMetaClientHooks (pollMs: number, onVisibleOrFocus: () => void, onPollTick: () => void) {
  if (pollMs > 0 && liveMetaPollTimer == null) {
    liveMetaPollTimer = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        onPollTick()
      }
    }, pollMs)
  }
  if (liveMetaVisibilityHandler == null && typeof document !== 'undefined') {
    liveMetaVisibilityHandler = () => {
      if (document.visibilityState === 'visible') onVisibleOrFocus()
    }
    document.addEventListener('visibilitychange', liveMetaVisibilityHandler)
  }
  if (liveMetaFocusHandler == null && typeof window !== 'undefined') {
    liveMetaFocusHandler = () => {
      onVisibleOrFocus()
    }
    window.addEventListener('focus', liveMetaFocusHandler)
  }
  if (liveMetaPageShowHandler == null && typeof window !== 'undefined') {
    liveMetaPageShowHandler = (event: PageTransitionEvent) => {
      if (event.persisted) onVisibleOrFocus()
    }
    window.addEventListener('pageshow', liveMetaPageShowHandler)
  }
}

export type IterableLiveById = Record<string, {
  showComponent?: boolean
  options?: Record<string, unknown>
  defaults?: Record<string, unknown>
  messageType?: unknown
  imageDesktopPlacement?: 'left' | 'right'
  iterablePlacement?: unknown
  setPlacementId?: unknown
}>

export type IterableLiveMetaResponse = {
  byId: IterableLiveById
  blocks: IterableLiveListItem[]
}

/**
 * Client-only live Strapi rows for Iterable **blocks** (layout: placement, messageType, visibility, options).
 * Campaign **copy/media** comes from the Iterable SDK elsewhere.
 * One shared `useAsyncData` key (Nuxt dedupes consumers); fetch always uses current `slug`.
 * On **slug** change we clear `data` and refresh immediately so debounced path updates cannot reuse another page’s live-meta.
 */
/** Non-empty string for `$fetch` query only; empty means caller must skip request (API returns 400 for missing slug). */
function iterableLiveMetaSlugQuery (slugCandidate: unknown): string {
  if (slugCandidate == null) return ''
  if (typeof slugCandidate === 'string') {
    return slugCandidate.trim()
  }
  if (typeof slugCandidate === 'number' && Number.isFinite(slugCandidate)) {
    return String(slugCandidate)
  }
  return ''
}

export function useIterableLiveBlocksMeta () {
  const slug = useIterableCmsPageSlug()
  const route = useRoute()
  const config = useRuntimeConfig()
  const debounceTime = Math.max(0, Number(config.public?.iterablePageFinishSyncDebounceMs) || 400)
  const pollMs = Math.max(0, Number(config.public?.iterableLiveMetaPollMs) || 0)

  /** Nuxt requires a static string key for dedupe across layout + blocks. Slug is read in the handler and on each `refresh()`. */
  const asyncResult = useAsyncData(
    'iterable-live-blocks-meta',
    async () => {
      const seq = ++liveMetaFetchSeq
      const empty: IterableLiveMetaResponse = { byId: {}, blocks: [] }
      const slugForQuery = iterableLiveMetaSlugQuery(slug.value)
      if (!slugForQuery) {
        if (seq !== liveMetaFetchSeq) {
          return cloneLiveMetaResponse(committedLiveMeta.value)
        }
        committedLiveMeta.value = empty
        return empty
      }
      try {
        const data = await $fetch<IterableLiveMetaResponse>('/api/iterable-live-blocks-meta', {
          query: { slug: slugForQuery }
        })
        if (seq !== liveMetaFetchSeq) {
          return cloneLiveMetaResponse(committedLiveMeta.value)
        }
        const normalized: IterableLiveMetaResponse = {
          byId: data?.byId ?? {},
          blocks: Array.isArray(data?.blocks) ? data.blocks : []
        }
        committedLiveMeta.value = cloneLiveMetaResponse(normalized)
        return normalized
      } catch (err) {
        logIterableError('iterable-live-blocks-meta $fetch', err)
        if (seq !== liveMetaFetchSeq) {
          return cloneLiveMetaResponse(committedLiveMeta.value)
        }
        committedLiveMeta.value = empty
        return empty
      }
    },
    {
      /** No SSR fetch — avoids stale payload hydration; client is source of truth for Strapi live rows. */
      server: false,
      immediate: import.meta.client,
      default: () => ({ byId: {}, blocks: [] })
    }
  )

  let debounceTimer: ReturnType<typeof setTimeout> | null = null

  const refreshSafe = async () => {
    if (!import.meta.client) return
    if (liveMetaRefreshInFlight) {
      liveMetaRefreshPending = true
      return
    }
    liveMetaRefreshInFlight = true
    try {
      await asyncResult.refresh()
    } catch (err) {
      console.error('[Iterable] Live fetch failed:', err)
      logIterableError('iterable-live-blocks-meta refresh', err)
    } finally {
      liveMetaRefreshInFlight = false
      if (liveMetaRefreshPending) {
        liveMetaRefreshPending = false
        /** Defer so `refresh()` completion does not synchronously re-enter and blow the stack if watchers fire during flush. */
        queueMicrotask(() => {
          void refreshSafe()
        })
      }
    }
  }

  const scheduleRefresh = () => {
    if (!import.meta.client) return
    if (debounceTimer) clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => {
      debounceTimer = null
      void refreshSafe()
    }, debounceTime)
  }

  const stopSlugWatch = watch(
    () => [slug.value, route.fullPath] as const,
    ([newSlug], old) => {
      const prevSlug = old?.[0]
      const slugChanged =
        import.meta.client && prevSlug !== undefined && newSlug !== prevSlug

      if (!slugChanged) {
        scheduleRefresh()
        return
      }

      if (debounceTimer) {
        clearTimeout(debounceTimer)
        debounceTimer = null
      }
      /** Invalidate any in-flight fetch still tied to the previous slug before clearing UI state. */
      liveMetaFetchSeq++
      committedLiveMeta.value = { byId: {}, blocks: [] }
      if (asyncResult.data) {
        asyncResult.data.value = { byId: {}, blocks: [] }
      }
      void refreshSafe()
    },
    { flush: 'post', immediate: import.meta.client }
  )

  if (import.meta.client) {
    liveMetaClientRefCount++
    if (liveMetaClientRefCount === 1) {
      const scheduleFocusVisibilityRefresh = () => {
        if (liveMetaImmediateDebounce != null) clearTimeout(liveMetaImmediateDebounce)
        liveMetaImmediateDebounce = setTimeout(() => {
          liveMetaImmediateDebounce = null
          void refreshSafe()
        }, 100)
      }
      startLiveMetaClientHooks(pollMs, scheduleFocusVisibilityRefresh, () => {
        void refreshSafe()
      })
    }
  }

  onScopeDispose(() => {
    stopSlugWatch()
    if (debounceTimer) clearTimeout(debounceTimer)
    debounceTimer = null

    if (import.meta.client) {
      liveMetaClientRefCount--
      if (liveMetaClientRefCount <= 0) {
        liveMetaClientRefCount = 0
        stopLiveMetaClientHooks()
      }
    }
  })

  return asyncResult
}
